import { useEffect, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "@sportscanner/ui";
import { clubInfoUpdateSchema } from "@sportscanner/schemas";
import { apiGet, apiPut, getApiErrorCode } from "@/lib/api";
import { isPhoneValid } from "@/lib/phone";
import { COUNTRIES, DEFAULT_COUNTRY } from "@/lib/countries";
import PhoneInput from "@/components/booking/phone-input";
import { useMe, useSubscriptionActive, type MeClub } from "@/lib/me";
import ConfirmDialog from "@/components/confirm-dialog";
import BookingImpactDialog from "@/components/booking-impact-dialog";
import LocationPicker, {
  type PickedLocation,
} from "@/components/location-picker";

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as
  | string
  | undefined;

interface Props {
  clubId: string;
}

type TabKey = "basic" | "more";

export default function ClubInfoForm({ clubId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { refresh: refreshMe } = useMe();
  const subscriptionActive = useSubscriptionActive();

  const clubQuery = useQuery({
    queryKey: ["admin", "clubs", clubId],
    queryFn: async () => {
      const data = await apiGet<{ club: MeClub }>(`/api/admin/clubs/${clubId}`);
      return data.club;
    },
  });

  const club = clubQuery.data;

  const [activeTab, setActiveTab] = useState<TabKey>("basic");

  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [workingHoursStart, setWorkingHoursStart] = useState("08:00");
  const [workingHoursEnd, setWorkingHoursEnd] = useState("22:00");
  const [bookingDaysInput, setBookingDaysInput] = useState("14");
  const [isFree, setIsFree] = useState(false);
  const [nameEn, setNameEn] = useState("");
  const [nameRu, setNameRu] = useState("");
  const [descriptionSr, setDescriptionSr] = useState("");
  const [descriptionEn, setDescriptionEn] = useState("");
  const [descriptionRu, setDescriptionRu] = useState("");

  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Hours-narrowing warn dialog — only opened when the preflight finds
  // upcoming bookings that fall outside the new window. Other edits route
  // through the existing generic confirm dialog.
  const [hoursWarn, setHoursWarn] = useState<{ count: number } | null>(null);
  const [preflighting, setPreflighting] = useState(false);

  // Reset form when the loaded club changes (e.g. SUPERADMIN picks a different club).
  useEffect(() => {
    if (!club) return;
    setAddress(club.address || "");
    setCity(club.city || "");
    setCountry(club.country || DEFAULT_COUNTRY);
    setLat(club.lat ?? null);
    setLng(club.lng ?? null);
    setPhone(club.phone || "");
    setWebsite(club.website || "");
    setWorkingHoursStart(club.workingHoursStart || "08:00");
    setWorkingHoursEnd(club.workingHoursEnd || "22:00");
    setBookingDaysInput(String(club.bookingDaysAdvance ?? 14));
    setIsFree(club.isFree ?? false);
    setNameEn(club.nameEn || "");
    setNameRu(club.nameRu || "");
    // descriptionSr is the canonical Serbian text; fall back to the legacy
    // unlocalized `description` column if migrated data only set that.
    setDescriptionSr(club.descriptionSr || club.description || "");
    setDescriptionEn(club.descriptionEn || "");
    setDescriptionRu(club.descriptionRu || "");
    setErrorCode(null);
  }, [club]);

  const bookingDaysNumber =
    bookingDaysInput === "" ? NaN : Number(bookingDaysInput);
  const bookingDaysValid =
    Number.isInteger(bookingDaysNumber) &&
    bookingDaysNumber >= 1 &&
    bookingDaysNumber <= 90;

  const phoneInvalid = phone.trim().length > 0 && !isPhoneValid(phone);
  const isValid =
    address.trim().length >= 3 &&
    city.trim().length >= 1 &&
    country.length === 2 &&
    isPhoneValid(phone) &&
    workingHoursStart < workingHoursEnd &&
    bookingDaysValid;

  const isDirty = club
    ? address !== (club.address || "") ||
      city !== (club.city || "") ||
      country !== (club.country || DEFAULT_COUNTRY) ||
      lat !== (club.lat ?? null) ||
      lng !== (club.lng ?? null) ||
      phone !== (club.phone || "") ||
      website !== (club.website || "") ||
      workingHoursStart !== (club.workingHoursStart || "08:00") ||
      workingHoursEnd !== (club.workingHoursEnd || "22:00") ||
      bookingDaysInput !== String(club.bookingDaysAdvance ?? 14) ||
      isFree !== (club.isFree ?? false) ||
      nameEn !== (club.nameEn || "") ||
      nameRu !== (club.nameRu || "") ||
      descriptionSr !== (club.descriptionSr || club.description || "") ||
      descriptionEn !== (club.descriptionEn || "") ||
      descriptionRu !== (club.descriptionRu || "")
    : false;

  const mutation = useMutation({
    mutationFn: async () => {
      const parsed = clubInfoUpdateSchema.safeParse({
        address: address.trim(),
        city: city.trim(),
        country,
        lat,
        lng,
        phone: phone.trim(),
        website: website.trim() || null,
        workingHoursStart,
        workingHoursEnd,
        bookingDaysAdvance: bookingDaysNumber,
        isFree,
        nameEn: nameEn.trim() || null,
        nameRu: nameRu.trim() || null,
        descriptionSr: descriptionSr.trim() || null,
        descriptionEn: descriptionEn.trim() || null,
        descriptionRu: descriptionRu.trim() || null,
      });
      if (!parsed.success) {
        throw { error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED" };
      }
      return apiPut<{ ok: true; club: MeClub }>(
        `/api/admin/clubs/${clubId}/info`,
        parsed.data,
      );
    },
    onSuccess: async () => {
      setErrorCode(null);
      toast.success(t("settings.savedToast"));
      await queryClient.invalidateQueries({
        queryKey: ["admin", "clubs", clubId],
      });
      await refreshMe();
    },
    onError: (err) => {
      setErrorCode(getApiErrorCode(err));
    },
  });

  // Only preflight when working hours actually narrow. Widening (or no change)
  // can never push existing bookings outside the window. Everything else
  // (address, phone, booking-days) goes straight to the generic confirm.
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || !isDirty || mutation.isPending || preflighting) return;
    const narrowed =
      !!club &&
      (workingHoursStart > (club.workingHoursStart || "08:00") ||
        workingHoursEnd < (club.workingHoursEnd || "22:00"));
    if (!narrowed) {
      setConfirmOpen(true);
      return;
    }
    setPreflighting(true);
    try {
      const params = new URLSearchParams({
        kind: "club-hours",
        workingHoursStart,
        workingHoursEnd,
      });
      const res = await apiGet<{ severity: string; count: number }>(
        `/api/admin/clubs/${clubId}/edit-impact?${params.toString()}`,
      );
      if (res.severity === "warn") {
        setHoursWarn({ count: res.count });
      } else {
        setConfirmOpen(true);
      }
    } catch {
      // Preflight failure shouldn't strand the user — fall back to the
      // generic confirm dialog. Server still backstops nothing here (no block
      // case for hours), so this just skips the friendly warn.
      setConfirmOpen(true);
    } finally {
      setPreflighting(false);
    }
  }

  function handleConfirm() {
    setConfirmOpen(false);
    setHoursWarn(null);
    mutation.mutate();
  }

  // The map picker is the primary way to set a location: it fills the address /
  // city / country fields (still editable below as a fallback) and the pin.
  function handleLocationChange(loc: PickedLocation) {
    setLat(loc.lat);
    setLng(loc.lng);
    if (loc.address) setAddress(loc.address);
    if (loc.city) setCity(loc.city);
    if (loc.country) setCountry(loc.country.toUpperCase());
  }

  if (clubQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!club) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t("errors.api.CLUB_NOT_FOUND")}</AlertDescription>
      </Alert>
    );
  }

  const saveDisabled =
    !isValid ||
    !isDirty ||
    mutation.isPending ||
    preflighting ||
    !subscriptionActive;

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        {errorCode && errorCode !== "PHONE_INVALID" && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabKey)}
        >
          <TabsList className="w-full">
            <TabsTrigger value="basic">{t("settings.tabs.basic")}</TabsTrigger>
            <TabsTrigger value="more">{t("settings.tabs.more")}</TabsTrigger>
          </TabsList>

          <TabsContent
            value="basic"
            className="space-y-6 mt-4 rounded-xl border border-neutral-200 bg-white p-5"
          >
            <LocationPicker
              apiKey={GOOGLE_MAPS_KEY}
              lat={lat}
              lng={lng}
              disabled={mutation.isPending}
              onChange={handleLocationChange}
            />

            <div className="space-y-2">
              <Label htmlFor="address" className="text-sm text-neutral-700">
                {t("onboarding.address.label")}{" "}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="address"
                type="text"
                placeholder={t("onboarding.address.placeholder")}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={mutation.isPending}
                className="rounded-lg border-neutral-200"
              />
              <p className="text-xs text-neutral-500">
                {t("onboarding.address.help")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city" className="text-sm text-neutral-700">
                  {t("onboarding.city.label")}{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="city"
                  type="text"
                  placeholder={t("onboarding.city.placeholder")}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  disabled={mutation.isPending}
                  className="rounded-lg border-neutral-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-neutral-700">
                  {t("onboarding.country.label")}{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={country}
                  onValueChange={setCountry}
                  disabled={mutation.isPending}
                >
                  <SelectTrigger className="rounded-lg border-neutral-200 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm text-neutral-700">
                {t("onboarding.phone.label")}{" "}
                <span className="text-red-500">*</span>
              </Label>
              <PhoneInput
                id="phone"
                value={phone}
                onChange={setPhone}
                disabled={mutation.isPending}
                invalid={phoneInvalid || errorCode === "PHONE_INVALID"}
              />
              {phoneInvalid || errorCode === "PHONE_INVALID" ? (
                <p className="text-xs text-red-500">
                  {t("errors.api.PHONE_INVALID")}
                </p>
              ) : (
                <p className="text-xs text-neutral-500">
                  {t("onboarding.phone.help")}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-neutral-700">
                {t("onboarding.workingHours.label")}{" "}
                <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-2 sm:gap-3">
                <Input
                  type="time"
                  value={workingHoursStart}
                  onChange={(e) => setWorkingHoursStart(e.target.value)}
                  disabled={mutation.isPending}
                  className="rounded-lg border-neutral-200 flex-1 sm:flex-initial sm:w-32"
                />
                <span className="text-neutral-400">—</span>
                <Input
                  type="time"
                  value={workingHoursEnd}
                  onChange={(e) => setWorkingHoursEnd(e.target.value)}
                  disabled={mutation.isPending}
                  className="rounded-lg border-neutral-200 flex-1 sm:flex-initial sm:w-32"
                />
              </div>
              <p className="text-xs text-neutral-500">
                {t("onboarding.workingHours.help")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bookingDays" className="text-sm text-neutral-700">
                {t("onboarding.bookingDays.label")}{" "}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="bookingDays"
                type="number"
                inputMode="numeric"
                min={1}
                max={90}
                placeholder="14"
                value={bookingDaysInput}
                onChange={(e) => setBookingDaysInput(e.target.value)}
                disabled={mutation.isPending}
                className="rounded-lg border-neutral-200 w-32"
              />
              <p className="text-xs text-neutral-500">
                {t("onboarding.bookingDays.help")}
              </p>
            </div>
          </TabsContent>

          <TabsContent
            value="more"
            className="space-y-6 mt-4 rounded-xl border border-neutral-200 bg-white p-5"
          >
            <div className="space-y-2">
              <Label htmlFor="website" className="text-sm text-neutral-700">
                {t("onboarding.website.label")}{" "}
                <span className="text-neutral-400 text-xs">
                  {t("common.optional")}
                </span>
              </Label>
              <Input
                id="website"
                type="url"
                placeholder="https://example.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                disabled={mutation.isPending}
                className="rounded-lg border-neutral-200"
              />
            </div>

            <div className="space-y-3 rounded-lg border border-neutral-200 p-4">
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-neutral-800">
                  {t("settings.nameTranslations.label")}
                </p>
                <p className="text-xs text-neutral-500">
                  {t("settings.nameTranslations.hint")}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="club-name-en"
                    className="text-xs text-neutral-600"
                  >
                    {t("settings.nameTranslations.english")}
                  </Label>
                  <Input
                    id="club-name-en"
                    type="text"
                    value={nameEn}
                    onChange={(e) => setNameEn(e.target.value)}
                    disabled={mutation.isPending}
                    className="rounded-lg border-neutral-200"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label
                    htmlFor="club-name-ru"
                    className="text-xs text-neutral-600"
                  >
                    {t("settings.nameTranslations.russian")}
                  </Label>
                  <Input
                    id="club-name-ru"
                    type="text"
                    value={nameRu}
                    onChange={(e) => setNameRu(e.target.value)}
                    disabled={mutation.isPending}
                    className="rounded-lg border-neutral-200"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-neutral-200 p-4">
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-neutral-800">
                  {t("settings.description.label")}
                </p>
                <p className="text-xs text-neutral-500">
                  {t("settings.description.hint")}
                </p>
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="club-description-sr"
                  className="text-xs text-neutral-600"
                >
                  {t("settings.description.primary")}
                </Label>
                <Textarea
                  id="club-description-sr"
                  value={descriptionSr}
                  onChange={(e) => setDescriptionSr(e.target.value)}
                  disabled={mutation.isPending}
                  maxLength={2000}
                  rows={3}
                  className="rounded-lg border-neutral-200"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="club-description-en"
                    className="text-xs text-neutral-600"
                  >
                    {t("settings.description.english")}
                  </Label>
                  <Textarea
                    id="club-description-en"
                    value={descriptionEn}
                    onChange={(e) => setDescriptionEn(e.target.value)}
                    disabled={mutation.isPending}
                    maxLength={2000}
                    rows={3}
                    className="rounded-lg border-neutral-200"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label
                    htmlFor="club-description-ru"
                    className="text-xs text-neutral-600"
                  >
                    {t("settings.description.russian")}
                  </Label>
                  <Textarea
                    id="club-description-ru"
                    value={descriptionRu}
                    onChange={(e) => setDescriptionRu(e.target.value)}
                    disabled={mutation.isPending}
                    maxLength={2000}
                    rows={3}
                    className="rounded-lg border-neutral-200"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start justify-between gap-3 rounded-lg border border-neutral-200 p-4">
              <div className="flex-1 min-w-0">
                <Label
                  htmlFor="isFree"
                  className="text-sm font-medium text-neutral-800"
                >
                  {t("settings.isFree.label")}
                </Label>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {t("settings.isFree.hint")}
                </p>
              </div>
              <Switch
                id="isFree"
                checked={isFree}
                onCheckedChange={setIsFree}
                disabled={mutation.isPending}
              />
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saveDisabled}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {mutation.isPending || preflighting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("common.saving")}
              </>
            ) : (
              t("common.save")
            )}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("settings.confirmSave.title")}
        description={t("settings.confirmSave.description")}
        confirmLabel={t("common.save")}
        isPending={mutation.isPending}
        onConfirm={handleConfirm}
      />

      <BookingImpactDialog
        open={!!hoursWarn}
        onOpenChange={(o) => {
          if (!o) setHoursWarn(null);
        }}
        severity="warn"
        count={hoursWarn?.count ?? 0}
        title={t("bookingImpact.clubHours.warnTitle")}
        description={t("bookingImpact.clubHours.warnDescription")}
        confirmLabel={t("bookingImpact.actions.saveAnyway")}
        isPending={mutation.isPending}
        onConfirm={handleConfirm}
      />
    </>
  );
}
